import React, { useState } from "react";
import Close from "@mui/icons-material/Close";
import { UrgentVisitStep } from "../../../state/urgent-visit";
import {
  useUrgentVisitStore,
} from "../../../hooks/redux-hooks/urgent-visit-store/urgent-visit.store";
import {
  useUrgentVisitNavigation,
  useUrgentVisitValidation,
  useUrgentVisitBooking,
} from "../../../hooks/urgent-visit";
import { ModalHeader } from "../../../components/ModalHeader/ModalHeader";
import { ProgressBar } from "../../../components/ProgressBar/ProgressBar";
import { ModalToolbar } from "../../../components/ModalToolbar/ModalToolbar";
import {
  MultiPetBanner,
} from "../../../components/MultiPetBanner/MultiPetBanner";
import { TimeSelectionStep } from "../../containers/UrgentVisitContainers/TimeSelectionStep";
import { ReasonSelectionStep } from "../../containers/UrgentVisitContainers/ReasonSelectionStep";
import { AdditionalNotesStep } from "../../containers/UrgentVisitContainers/AdditionalNotesStep";
import { ReviewStep } from "../../containers/UrgentVisitContainers/ReviewStep";
import { BookedStep } from "../../containers/UrgentVisitContainers/BookedStep";
import { PetSelectionStep } from "../../containers/UrgentVisitContainers/PetSelectionStep";
import {
  StyledBookingFlowButton,
} from "../../containers/BookingScheduleContainer/BookingScheduleContainer.style";
import { CalendarPlus, DoctorVisitIcon } from "../../../components/Icons";
import UrgentVisitIcon from "../../../components/Icons/UrgentVisitIcon";
import { useModal } from "../../../context/ModalContext";
import { MODALS } from "../../../config/params";
import { TermsText } from "../../../components/TermsAndConditions/TermsAndConditions";
import { userActivityLogger } from "../../../utils/mixpanel";
import { MIXPANEL_EVENTS } from "../../../config/mixPanelEvents";
import { useBookingAppointmentTypeStore } from "../../../hooks";

export const UrgentVisitModalContent = () => {
  const {
    currentStep, selectedPets, currentPetIndex, isMultiPetOwner,
    resetUrgentVisit, rescheduleAppointmentId, petAppointmentMap,
  } = useUrgentVisitStore();
  const { openModal } = useModal();
  const { setDefaultBookingType } = useBookingAppointmentTypeStore();
  const [showDoctorVisitConfirm, setShowDoctorVisitConfirm] = useState(false);

  const handleClose = () => {
    userActivityLogger(MIXPANEL_EVENTS.URGENT_VISIT_EXIT, {
      appointment_category: "urgent",
      step: currentStep,
    });
    openModal(MODALS.CANCEL_BOOK_A_VISIT);
  };

  const {
    goToNextStep, goToPreviousStep, canGoBack, showCloseButton, scrollRef,
  } = useUrgentVisitNavigation(handleClose);
  const { isCtaDisabled } = useUrgentVisitValidation();
  const { createUrgentBooking, loading: bookingLoading } =
    useUrgentVisitBooking();

  const isSinglePetOwner = !isMultiPetOwner;
  const isGroupedReschedule = Object.keys(petAppointmentMap).length > 1;
  const isSinglePetFlow = isSinglePetOwner || (!!rescheduleAppointmentId && !isGroupedReschedule);
  const isBookedStep = currentStep === UrgentVisitStep.BOOKED;
  const isReviewStep = currentStep === UrgentVisitStep.REVIEW;
  const isReschedule = !!rescheduleAppointmentId;

  const showMultiPetBanner = selectedPets.length > 1 && [
    UrgentVisitStep.TIME_SELECTION,
    UrgentVisitStep.REASON_SELECTION,
  ].includes(currentStep);

  const progress = getProgressInfo(
    currentStep,
    isSinglePetFlow,
    currentPetIndex,
    Math.max(selectedPets.length, 1),
  );

  const ctaText = isReviewStep ?
    (isReschedule ? "Confirm Reschedule" : "Confirm Booking") :
    "Continue";

  const handleCtaClick = () => {
    const stepEventMap: Partial<Record<UrgentVisitStep, string>> = {
      [UrgentVisitStep.PET_SELECTION]: MIXPANEL_EVENTS.URGENT_VISIT_SELECT_PET,
      [UrgentVisitStep.TIME_SELECTION]: MIXPANEL_EVENTS.URGENT_VISIT_SELECT_TIME,
      [UrgentVisitStep.REASON_SELECTION]: MIXPANEL_EVENTS.URGENT_VISIT_SELECT_REASON,
      [UrgentVisitStep.ADDITIONAL_NOTES]: MIXPANEL_EVENTS.URGENT_VISIT_ACKNOWLEDGEMENT,
      [UrgentVisitStep.REVIEW]: MIXPANEL_EVENTS.URGENT_VISIT_REVIEW_CONFIRM,
    };
    const event = stepEventMap[currentStep];
    if (event) {
      userActivityLogger(event, {
        appointment_category: "urgent",
        pet_name: selectedPets.map((p) => p.name).join(","),
        no_of_pets: selectedPets.length,
        owner_type: isMultiPetOwner ? "multipet" : "single_pet",
      });
    }

    if (isReviewStep) {
      createUrgentBooking();
    } else {
      goToNextStep();
    }
  };

  const handleRequestDoctorVisit = () => {
    setShowDoctorVisitConfirm(true);
  };

  const handleConfirmDoctorVisit = () => {
    setShowDoctorVisitConfirm(false);
    resetUrgentVisit();
    setDefaultBookingType();
    openModal(MODALS.UPDATED_BOOK_A_VISIT_MODAL);
  };

  const handleCancelDoctorVisit = () => {
    setShowDoctorVisitConfirm(false);
  };

  if (showDoctorVisitConfirm) {
    return (
      <div className="h-full flex flex-col justify-between px-3">
        <div className="flex items-center h-full">
          <div className="">
            <div className="flex justify-center">
              <div className="flex items-center justify-center bg-taupe-4 rounded-full p-4 h-fit">
                <DoctorVisitIcon width={60} height={60} />
              </div>
            </div>
            <div className="text-2xl font-bold text-center mt-2">
              Book a Doctor Visit?
            </div>
            <p className="text-light-black text-center mt-1.5 2xl:px-8">
              Switching to a Doctor Visit will cancel your Urgent Visit booking
              progress. What would you like to do?
            </p>
          </div>
        </div>
        <div className="space-y-3 text-center pb-1">
          <StyledBookingFlowButton
            color="primary"
            size="small"
            className="w-full"
            onClick={handleConfirmDoctorVisit}
          >
            Book a Doctor Visit
          </StyledBookingFlowButton>
          <StyledBookingFlowButton
            onClick={handleCancelDoctorVisit}
            color="primary"
            size="small"
            className="!bg-transparent !border !border-gray-4 !text-black w-full hover:!bg-gray-3 !my-4"
          >
            Continue Booking Urgent Visit
          </StyledBookingFlowButton>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col justify-between px-3">
      {!isBookedStep && (
        <ModalToolbar
          canGoBack={canGoBack}
          showClose={showCloseButton}
          onBack={goToPreviousStep}
          onClose={handleClose}
          closeIcon={<Close className="text-light-black cursor-pointer" />}
        />
      )}

      {!isBookedStep && (
        <ModalHeader
          icon={<UrgentVisitIcon width={31} height={31} />}
          title={isReschedule ? "Reschedule Urgent Visit" : "Urgent Visit"}
          subtitle={
            isReschedule ?
              "Select a new time for your pet's urgent visit." :
              "If your pet is unwell, select the symptoms that are occurring," +
                " and book the next available urgent slot."
          }
        />
      )}

      {!isBookedStep && (
        <div className="mt-2 mb-3 w-full px-2">
          <ProgressBar
            totalSegments={progress.totalSegments}
            filledSegments={progress.filledSegments}
            halfSegmentIndex={progress.halfSegmentIndex}
          />
        </div>
      )}

      <div className="mb-4 w-full px-2">
        {showMultiPetBanner && (
          <MultiPetBanner
            petNames={selectedPets.map((p) => p.name)}
          />
        )}
      </div>

      <div
        className="h-full flex flex-col gap-4 items-center
          min-h-0 overflow-y-auto !w-full px-2 scrollbar-thin"
        ref={scrollRef}
      >
        {renderStep(currentStep, handleRequestDoctorVisit)}
      </div>

      {!isBookedStep && (
        <div className="flex flex-col gap-2 pb-1 pt-1.5 mt-2">
          <StyledBookingFlowButton
            disabled={isCtaDisabled || bookingLoading}
            color="primary"
            size="small"
            className="w-full !text-sm !leading-[14px] !text-center"
            onClick={handleCtaClick}
            startIcon={
              isReviewStep ? <CalendarPlus /> : undefined
            }
          >
            {ctaText}
          </StyledBookingFlowButton>
          {isReviewStep && <TermsText />}
        </div>
      )}
    </div>
  );
};

const renderStep = (
  step: UrgentVisitStep,
  onRequestDoctorVisit?: () => void,
) => {
  switch (step) {
  case UrgentVisitStep.PET_SELECTION:
    return <PetSelectionStep />;
  case UrgentVisitStep.TIME_SELECTION:
    return <TimeSelectionStep />;
  case UrgentVisitStep.REASON_SELECTION:
    return <ReasonSelectionStep onRequestDoctorVisit={onRequestDoctorVisit} />;
  case UrgentVisitStep.ADDITIONAL_NOTES:
    return <AdditionalNotesStep />;
  case UrgentVisitStep.REVIEW:
    return <ReviewStep />;
  case UrgentVisitStep.BOOKED:
    return <BookedStep />;
  default:
    return null;
  }
};

const MULTI_PET_FIXED_STEPS_COUNT = 2;

export interface ProgressInfo {
  totalSegments: number;
  filledSegments: number;
  halfSegmentIndex: number | null;
}

export const getProgressStepCount = (
  isSinglePet: boolean,
  totalSelectedPets: number,
): number => {
  if (isSinglePet) return 3;

  return MULTI_PET_FIXED_STEPS_COUNT + totalSelectedPets + 1;
};

export const getProgressInfo = (
  step: UrgentVisitStep,
  isSinglePet: boolean,
  currentPetIndex: number,
  totalSelectedPets: number,
): ProgressInfo => {
  const totalSegments = getProgressStepCount(
    isSinglePet, totalSelectedPets,
  );

  if (isSinglePet) {
    switch (step) {
    case UrgentVisitStep.PET_SELECTION:
      return { totalSegments, filledSegments: 1, halfSegmentIndex: null };
    case UrgentVisitStep.TIME_SELECTION:
      return { totalSegments, filledSegments: 1, halfSegmentIndex: null };
    case UrgentVisitStep.REASON_SELECTION:
      return { totalSegments, filledSegments: 1, halfSegmentIndex: 1 };
    case UrgentVisitStep.ADDITIONAL_NOTES:
      return { totalSegments, filledSegments: 2, halfSegmentIndex: null };
    case UrgentVisitStep.REVIEW:
      return { totalSegments, filledSegments: 3, halfSegmentIndex: null };
    default:
      return { totalSegments, filledSegments: 0, halfSegmentIndex: null };
    }
  }

  switch (step) {
  case UrgentVisitStep.PET_SELECTION:
    return { totalSegments, filledSegments: 1, halfSegmentIndex: null };
  case UrgentVisitStep.TIME_SELECTION:
    return { totalSegments, filledSegments: 2, halfSegmentIndex: null };
  case UrgentVisitStep.REASON_SELECTION:
    return {
      totalSegments,
      filledSegments: MULTI_PET_FIXED_STEPS_COUNT + currentPetIndex,
      halfSegmentIndex: MULTI_PET_FIXED_STEPS_COUNT + currentPetIndex,
    };
  case UrgentVisitStep.ADDITIONAL_NOTES:
    return {
      totalSegments,
      filledSegments: MULTI_PET_FIXED_STEPS_COUNT + currentPetIndex + 1,
      halfSegmentIndex: null,
    };
  case UrgentVisitStep.REVIEW:
    return {
      totalSegments,
      filledSegments: totalSegments,
      halfSegmentIndex: null,
    };
  default:
    return { totalSegments, filledSegments: 0, halfSegmentIndex: null };
  }
};
